import { useRef, useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import BusinessLocations from './BusinessLocations';
import BusinessOfferings from './ServiceOfferings';
import BusinessUsers from './BusinessUsers';
import { useBusinessLocationTypes } from '../../hooks/useBusinessLocationTypes';
import { uploadFile } from '../../services/fileUploadService';
import { getAccessToken, getClientId, getUser } from '../../services/authService';
import { API_KEY, CLIENT_APP } from '../../services/clientConfig';
import './BusinessEntityProfile.css';

interface BusinessEntity {
  id: string;
  name: string;
  ein: string;
  status: 'ACTIVE' | 'PENDING_REVIEW' | 'SUSPENDED' | 'DRAFT' | 'APPROVED' | 'DELETED';
  description: string;
  logoFileId?: string;
  logo?: string;
  logoUrl?: string;
  logoAt?: string;
  businessLocationType: { id: string; name: string; code: string };
  contactInformation: {
    primaryEmail: string;
    secondaryEmail?: string;
    primaryPhone: string;
    secondaryPhone?: string;
    websiteUrl?: string;
    contactPersonName?: string;
    contactPersonTitle?: string;
  };
  locationInformation?: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    county?: string;
    state: string;
    zipCode: string;
    country: string;
    latitude?: number;
    longitude?: number;
    placeId?: string;
  };
  businessUsers?: Array<{
    userId?: string;
    firstName?: string;
    lastName?: string;
    preferredFirstName?: string;
    preferredLastName?: string;
    email?: string;
    role?: string;
    status?: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

const PROFILE_API_V1 = 'https://dev.oliveeb.com/olreadia-service/api/v1';
const authHeader = (token: string) => token.startsWith('Bearer ') ? token : `Bearer ${token}`;

const getStoredActorId = () => {
  return getUser()?.id;
};

const getOwnerLabel = (business: BusinessEntity) => {
  const owner = business.businessUsers?.find((record) => record.role === 'OWNER' && record.status !== 'REMOVED');
  if (!owner) return '';
  const name = [
    owner.preferredFirstName || owner.firstName,
    owner.preferredLastName || owner.lastName,
  ].filter(Boolean).join(' ');
  return name || owner.email || owner.userId || '';
};

const BusinessEntityProfile = () => {
  const { businessEntityId } = useParams<{ businessEntityId: string }>();
  const { isLoggedIn, isLoading: authLoading, user } = useAuth();
  const { locationTypes, loading: typesLoading, fetchLocationTypes } = useBusinessLocationTypes();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'locations' | 'offerings' | 'users'>('locations');
  const [business, setBusiness] = useState<BusinessEntity | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const hasFetched = useRef(false);

  const [formData, setFormData] = useState({
    name: '',
    ein: '',
    businessLocationTypeId: '',
    description: '',
    primaryEmail: '',
    secondaryEmail: '',
    primaryPhone: '',
    secondaryPhone: '',
    website: '',
    contactPersonName: '',
    contactPersonTitle: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    county: '',
    state: '',
    zipCode: '',
    country: 'US',
    latitude: '',
    longitude: '',
    placeId: '',
  });

  const fetchBusinessEntity = useCallback(async () => {
    // Read token directly from auth storage; don't rely on React state (user/isLoggedIn)
    // which may not have hydrated yet right after navigation from registration.
    const token = getAccessToken();
    if (!token) {
      navigate('/signin');
      return;
    }

    setIsLoading(true);
    try {
      const storedActorId = getStoredActorId();
      const clientId = await getClientId();

      const response = await fetch(
        `${PROFILE_API_V1}/business-entities/${businessEntityId}`,
        {
          headers: {
            Authorization: authHeader(token),
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'O-CLIENT-APP': CLIENT_APP,
            'O-API-KEY': API_KEY,
            'O-CLIENT-ID': clientId || '',
            ...(storedActorId && { 'O-ACTOR-ID': storedActorId }),
          },
        }
      );

      if (response.status === 401) {
        // Token expired — redirect to sign in
        navigate('/signin');
        return;
      }

      if (response.status === 404) {
        setBusiness(null);
        return;
      }

      if (response.ok) {
        const data = await response.json();
        const activeBusinessUsers = Array.isArray(data.businessUsers) ? data.businessUsers : [];
        const currentUserHasAccess =
          !storedActorId ||
          activeBusinessUsers.length === 0 ||
          activeBusinessUsers.some((record: any) => record.userId === storedActorId && record.status !== 'REMOVED');

        if (!currentUserHasAccess) {
          setBusiness(null);
          return;
        }

        setBusiness(data);
        setFormData({
          name: data.name || '',
          ein: data.ein || '',
          businessLocationTypeId:
            data.businessLocationType?.id ||
            data.businessLocationTypeId ||
            data.businessLocationType ||
            '',
          description: data.description || '',
          primaryEmail: data.contactInformation?.primaryEmail || '',
          secondaryEmail: data.contactInformation?.secondaryEmail || '',
          primaryPhone: data.contactInformation?.primaryPhone || '',
          secondaryPhone: data.contactInformation?.secondaryPhone || '',
          website: data.contactInformation?.websiteUrl || '',
          contactPersonName: data.contactInformation?.contactPersonName || '',
          contactPersonTitle: data.contactInformation?.contactPersonTitle || '',
          addressLine1: data.locationInformation?.addressLine1 || '',
          addressLine2: data.locationInformation?.addressLine2 || '',
          city: data.locationInformation?.city || '',
          county: data.locationInformation?.county || '',
          state: data.locationInformation?.state || '',
          zipCode: data.locationInformation?.zipCode || '',
          country: data.locationInformation?.country || 'US',
          latitude: data.locationInformation?.latitude !== undefined ? String(data.locationInformation.latitude) : '',
          longitude: data.locationInformation?.longitude !== undefined ? String(data.locationInformation.longitude) : '',
          placeId: data.locationInformation?.placeId || '',
        });
      } else {
        setBusiness(null);
      }
    } catch (error) {
      console.error('Error fetching business:', error);
      setBusiness(null);
    } finally {
      setIsLoading(false);
    }
  }, [businessEntityId, navigate]);

  useEffect(() => {
    fetchLocationTypes({ size: 100 });
  }, [fetchLocationTypes]);

  useEffect(() => {
    if (!businessEntityId) return;

    // FIX: Don't wait for React auth state (isLoggedIn / authLoading) to settle.
    // After registration, the token is already in auth storage, so read it directly.
    // This prevents "Business Not Found" immediately after registration because
    // authLoading is true and isLoggedIn is false for a brief moment.
    const token = getAccessToken();
    if (!token) {
      navigate('/signin');
      return;
    }

    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchBusinessEntity();
    }
  }, [businessEntityId, navigate, fetchBusinessEntity]);

  // Re-fetch if authLoading finishes and we still have no business data (edge case)
  useEffect(() => {
    if (!authLoading && isLoggedIn && businessEntityId && !isLoading && business === null) {
      hasFetched.current = false; // allow re-fetch
      fetchBusinessEntity();
    }
  }, [authLoading, isLoggedIn]);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const actorId = user?.id || getStoredActorId();
    if (!file || !actorId) return;

    setUploadingLogo(true);
    setMessage(null);
    try {
      const result = await uploadFile(actorId, file, {
        purpose: 'BUSINESS_LOGO',
        fileType: 'PHOTO',
        confirm: false,
      });

      const token = getAccessToken();
      if (!token) throw new Error('Not authenticated');
      const clientId = await getClientId();
      const response = await fetch(
        `${PROFILE_API_V1}/business-entities/${businessEntityId}`,
        {
          method: 'PATCH',
          headers: {
            Authorization: authHeader(token),
            'Content-Type': 'application/json',
            'O-CLIENT-APP': CLIENT_APP,
            'O-API-KEY': API_KEY,
            'O-CLIENT-ID': clientId || '',
            'O-ACTOR-ID': actorId,
          },
          body: JSON.stringify({ logo: result.fileId }),
        }
      );

      if (response.ok) {
        setMessage({ type: 'success', text: 'Logo uploaded successfully.' });
        fetchBusinessEntity();
      } else {
        throw new Error('Failed to update business logo.');
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.message || 'Failed to upload logo.' });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleUpdateBusiness = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const token = getAccessToken();
      if (!token) throw new Error('Not authenticated');
      const clientId = await getClientId();

      const payload: any = {
        name: formData.name,
        description: formData.description,
      };

      if (formData.ein) payload.ein = formData.ein;
      if (formData.businessLocationTypeId) payload.businessLocationTypeId = formData.businessLocationTypeId;

      if (formData.primaryEmail || formData.primaryPhone || formData.website) {
        payload.contactInformation = {};
        if (formData.primaryEmail) payload.contactInformation.primaryEmail = formData.primaryEmail;
        if (formData.secondaryEmail) payload.contactInformation.secondaryEmail = formData.secondaryEmail;
        if (formData.primaryPhone) payload.contactInformation.primaryPhone = formData.primaryPhone;
        if (formData.secondaryPhone) payload.contactInformation.secondaryPhone = formData.secondaryPhone;
        if (formData.website) payload.contactInformation.websiteUrl = formData.website;
        if (formData.contactPersonName) payload.contactInformation.contactPersonName = formData.contactPersonName;
        if (formData.contactPersonTitle) payload.contactInformation.contactPersonTitle = formData.contactPersonTitle;
      }

      if (formData.addressLine1 || formData.city || formData.state) {
        payload.locationInformation = {
          addressLine1: formData.addressLine1,
          addressLine2: formData.addressLine2,
          city: formData.city,
          county: formData.county,
          state: formData.state,
          zipCode: formData.zipCode,
          country: formData.country,
        };
        const latitude = Number(formData.latitude);
        const longitude = Number(formData.longitude);
        if (Number.isFinite(latitude)) payload.locationInformation.latitude = latitude;
        if (Number.isFinite(longitude)) payload.locationInformation.longitude = longitude;
        if (formData.placeId) payload.locationInformation.placeId = formData.placeId;
      }

      const response = await fetch(
        `${PROFILE_API_V1}/business-entities/${businessEntityId}`,
        {
          method: 'PATCH',
          headers: {
            Authorization: authHeader(token),
            'Content-Type': 'application/json',
            'O-CLIENT-APP': CLIENT_APP,
            'O-API-KEY': API_KEY,
            'O-CLIENT-ID': clientId || '',
            ...((user?.id || getStoredActorId()) && { 'O-ACTOR-ID': user?.id || getStoredActorId() }),
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        setMessage({ type: 'success', text: 'Business updated successfully!' });
        setIsEditing(false);
        fetchBusinessEntity();
        setTimeout(() => setMessage(null), 3000);
      } else {
        const error = await response.text();
        throw new Error(error);
      }
    } catch (error) {
      console.error('Update error:', error);
      setMessage({ type: 'error', text: 'Failed to update business' });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE': return <span className="status-badge status-active">Active</span>;
      case 'PENDING_REVIEW': return <span className="status-badge status-pending">Pending Review</span>;
      case 'APPROVED': return <span className="status-badge status-active">Approved</span>;
      case 'SUSPENDED': return <span className="status-badge status-suspended">Suspended</span>;
      case 'DELETED': return <span className="status-badge status-draft">Deleted</span>;
      default: return <span className="status-badge status-draft">Draft</span>;
    }
  };

  // Show spinner while fetching — never show "Not Found" prematurely
  if (isLoading) {
    return (
      <div className="business-profile-loading">
        <div className="spinner"></div>
        <p>Loading business profile...</p>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="business-profile-error">
        <h2>Business Not Found</h2>
        <p>The business you're looking for doesn't exist or you don't have access.</p>
        <button onClick={() => navigate('/account/business/register')}>Register a Business</button>
      </div>
    );
  }

  const ownerLabel = getOwnerLabel(business);

  return (
    <div className="business-entity-profile">
      {/* Header Section */}
      <div className="profile-header">
        <div className="header-logo-section">
          <div className="business-logo-wrapper">
            {business.logoUrl || business.logoAt ? (
              <img src={business.logoUrl || business.logoAt} alt="Business Logo" className="business-logo" />
            ) : (
              <div className="business-logo-placeholder">
                {business.name?.charAt(0) || 'B'}
              </div>
            )}
            <button
              className="btn-upload-logo"
              onClick={() => logoInputRef.current?.click()}
              disabled={uploadingLogo}
              title="Upload logo"
            >
              {uploadingLogo ? '...' : '📷'}
            </button>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handleLogoUpload}
            />
          </div>
        </div>

        <div className="header-info">
          <h1>{business.name}</h1>
          <div className="header-meta">
            {getStatusBadge(business.status)}
            <span className="business-id">ID: {business.id}</span>
            <span className="business-ein">EIN: {business.ein || 'Not provided'}</span>
            {ownerLabel && <span className="business-owner">Owner: {ownerLabel}</span>}
          </div>
        </div>

        <button className="btn-edit-business" onClick={() => setIsEditing(true)}>
          Edit Business Info
        </button>
      </div>

      {message && !isEditing && (
        <div className={`message-banner ${message.type}`}>{message.text}</div>
      )}

      {/* Tabs Navigation */}
      <div className="profile-tabs">
        <button className={`tab-btn ${activeTab === 'locations' ? 'active' : ''}`} onClick={() => setActiveTab('locations')}>
          Business Locations
        </button>
        <button className={`tab-btn ${activeTab === 'offerings' ? 'active' : ''}`} onClick={() => setActiveTab('offerings')}>
          Business Offerings
        </button>
        <button className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>
          Business Users
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'locations' && <BusinessLocations businessEntityId={businessEntityId!} />}
        {activeTab === 'offerings' && <BusinessOfferings businessEntityId={businessEntityId!} />}
        {activeTab === 'users' && <BusinessUsers businessEntityId={businessEntityId!} />}
      </div>

      {/* Edit Business Modal */}
      {isEditing && (
        <div className="modal-overlay">
          <div className="modal-content modal-large">
            <div className="modal-header">
              <h3>Edit Business Information</h3>
              <button className="modal-close" onClick={() => setIsEditing(false)}>×</button>
            </div>
            {message && (
              <div className={`message-banner ${message.type}`}>{message.text}</div>
            )}
            <form onSubmit={handleUpdateBusiness}>
              <div className="modal-body">
                <div className="form-section">
                  <h4>Basic Information</h4>
                  <div className="form-group">
                    <label>Business Name *</label>
                    <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>EIN (Tax ID)</label>
                    <input type="text" value={formData.ein} onChange={(e) => setFormData({ ...formData, ein: e.target.value })} />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Business Location Type</label>
                      <select
                        value={formData.businessLocationTypeId}
                        onChange={(e) => setFormData({ ...formData, businessLocationTypeId: e.target.value })}
                        disabled={typesLoading}
                      >
                        <option value="">{typesLoading ? 'Loading...' : 'Select type'}</option>
                        {locationTypes.map((type: any) => (
                          <option key={type.id} value={type.id}>
                            {type.label || type.name || type.code} {type.code ? `(${type.code})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} />
                  </div>
                </div>

                <div className="form-section">
                  <h4>Contact Information</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Primary Email</label>
                      <input type="email" value={formData.primaryEmail} onChange={(e) => setFormData({ ...formData, primaryEmail: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label>Secondary Email</label>
                      <input type="email" value={formData.secondaryEmail} onChange={(e) => setFormData({ ...formData, secondaryEmail: e.target.value })} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Primary Phone</label>
                      <input type="tel" value={formData.primaryPhone} onChange={(e) => setFormData({ ...formData, primaryPhone: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label>Secondary Phone</label>
                      <input type="tel" value={formData.secondaryPhone} onChange={(e) => setFormData({ ...formData, secondaryPhone: e.target.value })} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Website</label>
                    <input type="url" value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Contact Person Name</label>
                      <input type="text" value={formData.contactPersonName} onChange={(e) => setFormData({ ...formData, contactPersonName: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label>Contact Person Title</label>
                      <input type="text" value={formData.contactPersonTitle} onChange={(e) => setFormData({ ...formData, contactPersonTitle: e.target.value })} />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h4>Location</h4>
                  <div className="form-group">
                    <label>Street Address</label>
                    <input type="text" value={formData.addressLine1} onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Address Line 2</label>
                    <input type="text" value={formData.addressLine2} onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })} />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>City</label>
                      <input type="text" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label>State</label>
                      <input type="text" value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>ZIP Code</label>
                      <input type="text" value={formData.zipCode} onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label>County</label>
                      <input type="text" value={formData.county} onChange={(e) => setFormData({ ...formData, county: e.target.value })} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Country</label>
                      <select value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })}>
                        <option value="US">United States</option>
                        <option value="CA">Canada</option>
                        <option value="UK">United Kingdom</option>
                        <option value="AU">Australia</option>
                        <option value="UG">Uganda</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Place ID</label>
                      <input type="text" value={formData.placeId} onChange={(e) => setFormData({ ...formData, placeId: e.target.value })} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Latitude</label>
                      <input type="text" value={formData.latitude} onChange={(e) => setFormData({ ...formData, latitude: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label>Longitude</label>
                      <input type="text" value={formData.longitude} onChange={(e) => setFormData({ ...formData, longitude: e.target.value })} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setIsEditing(false)}>Cancel</button>
                <button type="submit" disabled={isLoading}>
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessEntityProfile;
